import { useState } from "react";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { CheckCircle, AlertCircle, Loader2, KeyRound, MapPinned, Phone, Banknote, MessageCircle, Star, XCircle, MapPin, Clock, Plus, Navigation, Map as MapIcon } from "lucide-react";
import DriverPickupMapDialog from "./DriverPickupMapDialog";
import { toast } from "sonner";
import ChatPanel from "../ChatPanel";
import ReviewModal from "../ReviewModal";
import ReviewBadge from "../ReviewBadge";
import TripChronometer from "../TripChronometer";

function log(...args) {
  if (process.env.NODE_ENV === "development") console.error(...args);
}

function getBookingTypeBadge(type) {
  return type === "ride" ? { text: "Covoiturage", cls: "bg-[#0A2540]/10 text-[#0A2540]" } : { text: "Location", cls: "bg-[#FACC15]/20 text-[#0A2540]" };
}

function paymentBadge(status) {
  if (status === "paid") return { text: "Paye", cls: "bg-[#10B981]/10 text-[#10B981]" };
  if (status === "reserved") return { text: "Reserve", cls: "bg-[#3B82F6]/10 text-[#3B82F6]" };
  return { text: "Non paye", cls: "bg-[#EF4444]/10 text-[#EF4444]" };
}

export default function DriverBookingCard({ booking, onUpdate, onStartTracking, onStopTracking, trackingBookingId, currentLocation, currentUserId }) {
  const b = booking;
  const [otpInput, setOtpInput] = useState("");
  const [otpError, setOtpError] = useState("");
  const [loading, setLoading] = useState(false);
  const [extending, setExtending] = useState(false);
  const [confirmingPayment, setConfirmingPayment] = useState(false);

  const extendHourly = async () => {
    if (!window.confirm("Ajouter 15 minutes à cette location ? Le tarif de dépassement sera automatiquement appliqué.")) return;
    setExtending(true);
    try {
      const { data } = await api.post(`/express/${b.id}/extend`);
      toast.success(`+15 min ajoutées (+${(data.added_fee || 0).toLocaleString("fr-FR")} F)`, {
        description: `Nouveau total : ${(data.total_amount || 0).toLocaleString("fr-FR")} F`,
      });
      onUpdate?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setExtending(false); }
  };
  const [completingTrip, setCompletingTrip] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [showPickupMap, setShowPickupMap] = useState(false);

  const typeBadge = getBookingTypeBadge(b.booking_type);
  const pBadge = paymentBadge(b.payment_status);
  const isPaid = b.payment_status === "paid";
  const isReserved = b.payment_status === "reserved";
  const isTracking = trackingBookingId === b.id;

  const verifyOtp = async () => {
    if (otpInput.length !== 4) { setOtpError("Le code doit etre de 4 chiffres"); return; }
    setLoading(true);
    try {
      await api.post(`/bookings/${b.id}/verify-otp`, { otp: otpInput });
      setOtpError(""); setOtpInput("");
      // Auto-start GPS tracking so distance is captured for the whole trip.
      if (typeof onStartTracking === "function" && !isTracking) {
        try { onStartTracking(b.id); } catch (e) { /* noop */ }
      }
      onUpdate();
    } catch (e) {
      setOtpError(e.response?.data?.detail || "Code incorrect");
    } finally { setLoading(false); }
  };

  const confirmPayment = async () => {
    setConfirmingPayment(true);
    try {
      await api.post(`/bookings/${b.id}/confirm-payment`);
      onUpdate();
    } catch (e) {
      alert(e.response?.data?.detail || "Erreur");
    } finally { setConfirmingPayment(false); }
  };

  const completeTrip = async () => {
    setCompletingTrip(true);
    try {
      await api.post(`/bookings/${b.id}/complete`);
      // Auto-stop GPS tracking when the trip ends.
      if (typeof onStopTracking === "function" && isTracking) {
        try { onStopTracking(); } catch (e) { /* noop */ }
      }
      onUpdate();
    } catch (e) {
      alert(e.response?.data?.detail || "Erreur");
    } finally { setCompletingTrip(false); }
  };

  const cancelBooking = async () => {
    if (!window.confirm("Voulez-vous vraiment annuler cette reservation ?")) return;
    setCancelling(true);
    try {
      await api.post(`/bookings/${b.id}/cancel`);
      onUpdate();
    } catch (e) {
      alert(e.response?.data?.detail || "Erreur");
    } finally { setCancelling(false); }
  };

  return (
    <div className="bg-white rounded-xl border border-[#CBD5E1] p-4" data-testid={`driver-booking-${b.id}`}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${typeBadge.cls}`}>{typeBadge.text}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${pBadge.cls}`}>
              {pBadge.text}
            </span>
            {b.otp_verified && <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-[#10B981]/10 text-[#10B981] flex items-center gap-0.5"><CheckCircle className="w-3 h-3" /> OTP verifie</span>}
            {b.status === "in_progress" && <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-[#0A2540]/10 text-[#0A2540]">En cours</span>}
          </div>
          <p className="text-sm text-[#0A2540] font-medium">Client: {b.user_name}</p>
          <div className="text-xs text-[#64748B] flex items-center gap-2">
            <Phone className="w-3 h-3" /> {b.user_phone ? <a href={`tel:${b.user_phone}`} className="text-[#0A2540] underline hover:text-[#FACC15] transition-colors" data-testid={`call-client-${b.id}`}>{b.user_phone}</a> : "N/A"}
            {b.user_rating > 0 && <ReviewBadge rating={b.user_rating} totalReviews={b.user_total_reviews} />}
          </div>
          <p className="text-xs text-[#64748B]">{b.total_amount?.toLocaleString()} XAF | {b.created_at?.split("T")[0]}</p>
          {(b.pickup_address || b.pickup_neighborhood || b.pickup_lat) && (
            <div
              className={`mt-2 p-2 rounded-lg bg-[#FACC15]/10 border border-[#FACC15]/40 text-xs text-[#0A2540] ${b.pickup_lat && b.pickup_lng ? "cursor-pointer hover:bg-[#FACC15]/20 transition" : ""}`}
              data-testid={`driver-pickup-address-${b.id}`}
              onClick={b.pickup_lat && b.pickup_lng ? () => setShowPickupMap(true) : undefined}
              role={b.pickup_lat && b.pickup_lng ? "button" : undefined}
            >
              <div className="flex items-start gap-1.5">
                <MapPin className="w-3.5 h-3.5 text-[#FACC15] mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <strong>Lieu de prise en charge</strong>
                    {b.pickup_lat && b.pickup_lng && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-[#0A2540] text-white text-[10px] font-bold" data-testid={`driver-pickup-tap-hint-${b.id}`}>
                        <MapIcon className="w-3 h-3" /> Tap pour la carte
                      </span>
                    )}
                  </div>
                  {b.pickup_neighborhood && <div>Quartier : <span className="font-medium">{b.pickup_neighborhood}</span></div>}
                  {b.pickup_address && <div>Détail : <span className="font-medium">{b.pickup_address}</span></div>}
                  {b.pickup_lat && b.pickup_lng ? (
                    <div className="text-[10px] text-[#10B981] font-semibold mt-0.5">
                      📍 Position GPS partagée par le passager
                    </div>
                  ) : null}
                </div>
              </div>
              {b.pickup_lat && b.pickup_lng ? (
                <div className="mt-2 flex gap-1.5 flex-wrap" onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    onClick={() => setShowPickupMap(true)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#FACC15] text-[#0A2540] text-[11px] font-bold hover:bg-[#FACC15]/80 transition"
                    data-testid={`driver-pickup-view-map-${b.id}`}
                  >
                    <MapIcon className="w-3 h-3" /> Voir sur la carte
                  </button>
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=${b.pickup_lat},${b.pickup_lng}&travelmode=driving`}
                    target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#0A2540] text-white text-[11px] font-semibold hover:bg-[#0A2540]/90 transition"
                    data-testid={`driver-navigate-pickup-gmaps-${b.id}`}
                  >
                    <Navigation className="w-3 h-3" /> Google Maps
                  </a>
                  <a
                    href={`https://waze.com/ul?ll=${b.pickup_lat},${b.pickup_lng}&navigate=yes`}
                    target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#33CCFF] text-white text-[11px] font-semibold hover:bg-[#33CCFF]/90 transition"
                    data-testid={`driver-navigate-pickup-waze-${b.id}`}
                  >
                    <Navigation className="w-3 h-3" /> Waze
                  </a>
                </div>
              ) : null}
            </div>
          )}
          {/* Destination — always show whenever we know it (rental or express) */}
          {(b.destination_city || b.dropoff_address || b.hourly_destination_custom || b.dropoff_lat) && (
            <div className="mt-2 p-2 rounded-lg bg-[#10B981]/10 border border-[#10B981]/40 text-xs text-[#0A2540]" data-testid={`driver-destination-${b.id}`}>
              <div className="flex items-start gap-1.5">
                <MapPinned className="w-3.5 h-3.5 text-[#10B981] mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <strong>Destination :</strong>{" "}
                  <span className="font-medium">
                    {b.is_airport_transfer && "✈️ "}
                    {b.destination_city || b.dropoff_address || b.hourly_destination_custom}
                  </span>
                  {b.is_airport_transfer && (
                    <div className="text-[10px] text-[#10B981] font-semibold mt-0.5">Transfert aéroport · 20 000 F forfait</div>
                  )}
                </div>
              </div>
              {b.dropoff_lat && b.dropoff_lng ? (
                <div className="mt-2 flex gap-1.5 flex-wrap">
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=${b.dropoff_lat},${b.dropoff_lng}&travelmode=driving`}
                    target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#10B981] text-white text-[11px] font-semibold hover:bg-[#10B981]/90 transition"
                    data-testid={`driver-navigate-dropoff-gmaps-${b.id}`}
                  >
                    <Navigation className="w-3 h-3" /> Google Maps
                  </a>
                  <a
                    href={`https://waze.com/ul?ll=${b.dropoff_lat},${b.dropoff_lng}&navigate=yes`}
                    target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#33CCFF] text-white text-[11px] font-semibold hover:bg-[#33CCFF]/90 transition"
                    data-testid={`driver-navigate-dropoff-waze-${b.id}`}
                  >
                    <Navigation className="w-3 h-3" /> Waze
                  </a>
                </div>
              ) : null}
            </div>
          )}
          {(b.start_time || b.end_time) && b.booking_type === "rental" && (
            <div className="mt-2 p-2 rounded-lg bg-[#0A2540]/5 border border-[#0A2540]/20 flex items-center gap-1.5 text-xs text-[#0A2540]" data-testid={`driver-times-${b.id}`}>
              <Clock className="w-3.5 h-3.5 text-[#0A2540] shrink-0" />
              <div>
                {b.start_time && <span><strong>Heure de départ :</strong> {b.start_time}</span>}
                {b.start_time && b.end_time && <span className="mx-1">·</span>}
                {b.end_time && <span><strong>Heure de retour :</strong> {b.end_time}</span>}
              </div>
            </div>
          )}
          {b.booking_type === "rental" && (b.start_date || b.end_date) && (
            <div className="mt-2 p-2 rounded-lg bg-[#0A2540]/5 border border-[#0A2540]/20 flex items-center gap-1.5 text-xs text-[#0A2540]" data-testid={`driver-dates-${b.id}`}>
              <Clock className="w-3.5 h-3.5 text-[#0A2540] shrink-0" />
              <div>
                <strong>Date de réservation :</strong>{" "}
                {b.start_date || "?"}{b.end_date && b.end_date !== b.start_date ? ` → ${b.end_date}` : ""}
              </div>
            </div>
          )}
          <TripChronometer booking={b} role="driver" compact />
          {/* Completed recap — total charged, breakdown for hourly overtime */}
          {b.status === "completed" && (
            <div className="mt-2 p-3 rounded-lg bg-[#10B981]/10 border border-[#10B981]/40" data-testid={`driver-completed-total-${b.id}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-[#065F46] flex items-center gap-1">
                  <CheckCircle className="w-3.5 h-3.5" /> Course terminée · total encaissé
                </span>
                <span className="text-lg font-extrabold text-[#065F46]" style={{ fontFamily: "Outfit" }}>
                  {(b.total_amount || 0).toLocaleString("fr-FR")} F
                </span>
              </div>
              {(b.extra_minutes || 0) > 0 && (
                <div className="mt-1.5 text-[11px] text-[#065F46] flex justify-between border-t border-[#10B981]/30 pt-1.5">
                  <span>Base : {(b.original_forfait_price || 0).toLocaleString("fr-FR")} F</span>
                  <span>+ Dépassement : +{b.extra_minutes} min · +{(b.overtime_fee || 0).toLocaleString("fr-FR")} F</span>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2">
          {/* Accept/Refuse */}
          {b.status === "pending" && (
            <div className="flex gap-2">
              <Button size="sm" onClick={() => onUpdate(b.id, "confirmed")} className="bg-[#10B981] text-white hover:bg-[#059669]" data-testid={`confirm-booking-${b.id}`}>Accepter</Button>
              <Button size="sm" variant="outline" onClick={cancelBooking} disabled={cancelling} className="border-[#EF4444] text-[#EF4444]" data-testid={`refuse-booking-${b.id}`}>
                {cancelling ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <XCircle className="w-3.5 h-3.5 mr-1" />}
                Refuser
              </Button>
            </div>
          )}

          {/* Step 1: OTP verification FIRST (when booking is confirmed, payment reserved or not) */}
          {b.status === "confirmed" && !b.otp_verified && (
            <div className="flex flex-col gap-1.5" data-testid={`otp-verify-section-${b.id}`}>
              <div className="flex items-center gap-1.5">
                <KeyRound className="w-4 h-4 text-[#0A2540]" />
                <span className="text-xs font-semibold text-[#0A2540]">Entrez le code OTP du client</span>
              </div>
              <div className="flex gap-2">
                <Input
                  value={otpInput}
                  onChange={e => setOtpInput(e.target.value.replace(/\D/g, "").slice(0, 4))}
                  placeholder="Code 4 chiffres"
                  className="h-9 w-32 border-[#CBD5E1] text-center font-mono text-lg tracking-widest"
                  maxLength={4}
                  data-testid={`otp-input-${b.id}`}
                />
                <Button size="sm" onClick={verifyOtp} disabled={loading} className="bg-[#0A2540] text-white hover:bg-[#081b2e]" data-testid={`verify-otp-btn-${b.id}`}>Verifier</Button>
              </div>
              {otpError && <p className="text-xs text-[#EF4444]">{otpError}</p>}
            </div>
          )}

          {/* Step 2: Confirm payment AFTER OTP verified (when reserved) */}
          {b.otp_verified && isReserved && (
            <Button size="sm" onClick={confirmPayment} disabled={confirmingPayment} className="bg-[#FACC15] text-[#0A2540] hover:bg-[#EAB308] font-semibold" data-testid={`confirm-payment-${b.id}`}>
              {confirmingPayment ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Banknote className="w-3.5 h-3.5 mr-1" />}
              Montant recu conforme
            </Button>
          )}

          {/* Waiting for passenger to reserve (cash) */}
          {b.status === "confirmed" && !b.otp_verified && !isPaid && !isReserved && (
            <span className="text-xs text-[#EAB308] font-medium flex items-center gap-1"><AlertCircle className="w-3 h-3" /> En attente de reservation du client</span>
          )}

          {/* Cancel button - for confirmed/in_progress bookings (not pending, already has Refuser) */}
          {b.status !== "completed" && b.status !== "cancelled" && b.status !== "pending" && (
            <Button size="sm" variant="outline" onClick={cancelBooking} disabled={cancelling} className="border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444]/10" data-testid={`cancel-booking-driver-${b.id}`}>
              {cancelling ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <XCircle className="w-3.5 h-3.5 mr-1" />}
              Annuler
            </Button>
          )}

          {/* Chat button */}
          <Button size="sm" variant="outline" onClick={() => setShowChat(!showChat)} className="border-[#CBD5E1] text-[#0A2540]" data-testid={`chat-toggle-${b.id}`}>
            <MessageCircle className="w-3.5 h-3.5 mr-1" /> {showChat ? "Fermer" : "Message"}
          </Button>

          {/* GPS tracking */}
          {b.status === "in_progress" && b.otp_verified && (
            <div className="flex flex-col gap-1.5">
              {isTracking ? (
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#10B981]/10 border border-[#10B981]/30">
                    <div className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse" />
                    <span className="text-xs font-medium text-[#10B981]">GPS actif</span>
                    {currentLocation && <span className="text-[10px] text-[#64748B]">{currentLocation.lat.toFixed(4)}, {currentLocation.lng.toFixed(4)}</span>}
                  </div>
                  <Button size="sm" variant="outline" onClick={() => onStopTracking()} className="border-[#EF4444] text-[#EF4444] h-7 text-xs" data-testid={`stop-tracking-${b.id}`}>Arreter</Button>
                </div>
              ) : (
                <Button size="sm" onClick={() => onStartTracking(b.id)} className="bg-[#0A2540] text-white hover:bg-[#081b2e]" data-testid={`start-tracking-${b.id}`}>
                  <MapPinned className="w-3.5 h-3.5 mr-1" /> Partager ma position
                </Button>
              )}
              <Button size="sm" onClick={completeTrip} disabled={completingTrip} className="bg-[#10B981] text-white hover:bg-[#059669] font-semibold" data-testid={`complete-trip-${b.id}`}>
                {completingTrip ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <CheckCircle className="w-3.5 h-3.5 mr-1" />}
                Voyage termine
              </Button>
              {b.rental_mode === "hourly" && !b.is_airport_transfer && (
                <Button size="sm" variant="outline" onClick={extendHourly} disabled={extending} className="border-[#FACC15] text-[#854D0E] bg-[#FEF9C3] hover:bg-[#FACC15]/30" data-testid={`extend-hourly-${b.id}`}>
                  {extending ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Plus className="w-3.5 h-3.5 mr-1" />}
                  Ajouter 15 min
                </Button>
              )}
            </div>
          )}

          {/* Review button for completed bookings */}
          {b.status === "completed" && !b.has_reviewed && b.user_id && (
            <Button size="sm" onClick={() => setShowReview(true)} className="bg-[#FACC15] text-[#0A2540] hover:bg-[#EAB308] font-semibold" data-testid={`review-passenger-${b.id}`}>
              <Star className="w-3 h-3 mr-1" /> Noter le client
            </Button>
          )}
          {b.status === "completed" && b.has_reviewed && (
            <span className="text-xs text-[#64748B] flex items-center gap-1">
              <Star className="w-3 h-3 fill-[#FACC15] text-[#FACC15]" /> Note envoyee
            </span>
          )}
        </div>
      </div>

      {/* Chat Panel */}
      {showChat && (
        <div className="mt-3 border-t border-[#CBD5E1] pt-3">
          <ChatPanel
            bookingId={b.id}
            currentUserId={currentUserId}
            otherUserName={b.user_name}
            onClose={() => setShowChat(false)}
          />
        </div>
      )}

      {/* Review Modal */}
      <ReviewModal
        open={showReview}
        onClose={() => setShowReview(false)}
        bookingId={b.id}
        targetUserId={b.user_id}
        targetUserName={b.user_name}
        onReviewed={() => onUpdate()}
      />
      <DriverPickupMapDialog
        open={showPickupMap}
        onOpenChange={setShowPickupMap}
        pickup={b.pickup_lat && b.pickup_lng ? { lat: b.pickup_lat, lng: b.pickup_lng, address: b.pickup_address || b.pickup_neighborhood || b.pickup_location } : null}
        passengerName={b.user_name}
        bookingLabel={b.pickup_address || b.pickup_neighborhood}
        bookingId={b.id}
      />
    </div>
  );
}
